import { useState, useEffect } from 'react';
import {
    Banknote, Package, Users, Zap, Building2, Sigma, FileDown, ChevronLeft, ChevronRight,
} from 'lucide-react';
import FinanceAopPrijmy from './FinanceAopPrijmy';
import FinanceAopMaterial from './FinanceAopMaterial';
import FinanceAopPersonal from './FinanceAopPersonal';
import FinanceAopEnergie from './FinanceAopEnergie';
import FinanceAopOstatni from './FinanceAopOstatni';
import FinanceAopSouhrn from './FinanceAopSouhrn';
import FinanceAopVarianty from './FinanceAopVarianty';
import MesicniGraf from '../components/finance/MesicniGraf';
import { ACCENT, OK, BAD, ORANGE, VIOLET, TEAL } from '../lib/uiTokens';

const fmtKc = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v));

/**
 * KPI pruh nad vrstvami AOP — roční souhrn plánu (příjmy / náklady / EBITDA /
 * marže) vždy na očích při editaci vrstev. Data ze `/finance/aop/souhrn`
 * (příjmy.celkem, variabilni.celkem, ostatni.celkem = měsíční pole).
 */
// Pravý panel statistik AOP plánu (jako v Bance/Dokladech) — EBITDA hero, KPI
// dlaždice (příjmy/náklady/food/labor) a DVA měsíční grafy (příjmy vs náklady +
// EBITDA), které rostou a vyplní celý pruh až dolů.
function AopStatistikyPanel({ rok, variantaId, cardStyle }) {
    const [d, setD] = useState(null);
    useEffect(() => {
        const q = new URLSearchParams({ rok });
        if (variantaId) q.set('varianta_id', variantaId);
        fetch('/api/finance/aop/souhrn?' + q.toString(), { credentials: 'same-origin' })
            .then(r => r.json()).then(setD).catch(() => {});
    }, [rok, variantaId]);
    if (!d) return <div className="rounded-2xl p-4 text-sm" style={{ ...cardStyle, color: 'var(--text-tertiary)' }}>Načítám statistiky…</div>;
    const sum = (a) => (Array.isArray(a) ? a.reduce((s, x) => s + (Number(x) || 0), 0) : 0);
    const nakladyM = Array.from({ length: 12 }, (_, i) => (Number(d.variabilni?.celkem?.[i]) || 0) + (Number(d.ostatni?.celkem?.[i]) || 0));
    const prijmy = sum(d.prijmy?.celkem);
    const naklady = nakladyM.reduce((s, x) => s + x, 0);
    const ebitda = prijmy - naklady;
    const marze = prijmy > 0 ? (ebitda / prijmy * 100) : 0;
    // Labor / Food cost z variabilních skupin (dle labelu z buildAopSouhrn).
    const skup = d.variabilni?.skupiny || [];
    const sumSkup = (label) => { const s = skup.find(x => x.label === label); return s ? sum(s.m) : 0; };
    const labor = sumSkup('Lidé');
    const food = sumSkup('Materiál');
    const pct = (v) => (prijmy > 0 ? `${(v / prijmy * 100).toFixed(1)} %` : '—');
    const grafData = Array.from({ length: 12 }, (_, idx) => ({
        mesic: `${rok}-${String(idx + 1).padStart(2, '0')}`,
        prijmy: Number(d.prijmy?.celkem?.[idx]) || 0,
        naklady: nakladyM[idx],
    }));
    const ebitdaData = Array.from({ length: 12 }, (_, idx) => ({
        mesic: `${rok}-${String(idx + 1).padStart(2, '0')}`,
        ebitda: (Number(d.prijmy?.celkem?.[idx]) || 0) - nakladyM[idx],
    }));
    const Karta = ({ children, className = '' }) => <div className={`rounded-2xl p-5 ${className}`} style={cardStyle}>{children}</div>;
    const MiniKpi = ({ label, val, sub, color }) => (
        <div>
            <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{label}</div>
            <div className="text-lg font-bold tabular-nums leading-tight" style={{ color: color || 'var(--text-primary)' }}>{val}</div>
            {sub && <div className="text-[11px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>{sub}</div>}
        </div>
    );
    return (
        <div className="flex flex-col gap-3 h-full">
            {/* Hero EBITDA */}
            <Karta className="flex-shrink-0">
                <div className="text-xs uppercase mb-1.5 tracking-wide font-bold" style={{ color: 'var(--text-tertiary)' }}>EBITDA plán (rok {rok})</div>
                <div className="text-4xl font-bold tabular-nums leading-none" style={{ color: ebitda >= 0 ? OK : BAD }}>{ebitda >= 0 ? '+' : '−'}{fmtKc(Math.abs(ebitda))} Kč</div>
                <div className="text-sm mt-2" style={{ color: 'var(--text-tertiary)' }}>Marže {marze.toFixed(1)} %</div>
            </Karta>

            {/* KPI dlaždice */}
            <Karta className="flex-shrink-0">
                <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                    <MiniKpi label="Příjmy" val={`${fmtKc(prijmy)} Kč`} color={OK} />
                    <MiniKpi label="Náklady" val={`${fmtKc(naklady)} Kč`} color={BAD} />
                    <MiniKpi label="Food / Material cost" val={`${fmtKc(food)} Kč`} sub={pct(food) + ' tržeb'} color={ORANGE} />
                    <MiniKpi label="Labor cost" val={`${fmtKc(labor)} Kč`} sub={pct(labor) + ' tržeb'} color={VIOLET} />
                </div>
            </Karta>

            {/* Graf 1 — příjmy vs náklady (roste) */}
            <Karta className="flex-1 min-h-0 flex flex-col">
                <div className="text-xs uppercase mb-3 tracking-wide font-bold flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>Příjmy vs náklady</div>
                <div className="flex-1 min-h-0">
                    <MesicniGraf data={grafData} rok={rok} fill
                        series={[{ key: 'prijmy', label: 'Příjmy', color: OK }, { key: 'naklady', label: 'Náklady', color: BAD }]} />
                </div>
            </Karta>

            {/* Graf 2 — EBITDA po měsících (roste) */}
            <Karta className="flex-1 min-h-0 flex flex-col">
                <div className="text-xs uppercase mb-3 tracking-wide font-bold flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>EBITDA po měsících</div>
                <div className="flex-1 min-h-0">
                    <MesicniGraf data={ebitdaData} rok={rok} fill
                        series={[{ key: 'ebitda', label: 'EBITDA', color: ACCENT }]} />
                </div>
            </Karta>
        </div>
    );
}

// Každá vrstva má vlastní barvu pro rychlou orientaci — číselný odznak a ikona
// ji nesou i v neaktivním stavu, aktivní vrstva se celá zabarví do jejího tintu.
const VRSTVY = [
    { key: 'prijmy',     label: 'Příjmy per kanál',    Icon: Banknote,  color: OK },
    { key: 'material',   label: 'Materiálové náklady', Icon: Package,   color: ORANGE },
    { key: 'personal',   label: 'Personální náklady',  Icon: Users,     color: VIOLET },
    { key: 'energie',    label: 'Energie',             Icon: Zap,       color: '#eab308' },
    { key: 'ostatni',    label: 'Ostatní náklady',     Icon: Building2, color: TEAL },
    { key: 'souhrn',     label: 'Souhrnný plán',       Icon: Sigma,     color: ACCENT },
];

export default function FinanceAop({ rok, setRok, variantaId, setVariantaId, isAdmin = false, cardStyle, isDark }) {
    const [vrstva, setVrstva] = useState('prijmy');

    const exportPdf = () => {
        const q = new URLSearchParams({ rok });
        if (variantaId) q.set('varianta_id', variantaId);
        window.open(`/api/finance/aop/export-pdf?${q.toString()}`, '_blank');
    };

    return (
        <div className="flex flex-col xl:flex-row gap-3" style={{ height: '100%' }}>
            {/* Levý sloupec: navigátor (fixní) + vrstva (tabulka vyplní výšku) */}
            <div className="flex flex-col flex-1 min-w-0 min-h-0">
            {/* Jednotná AOP hlavička v jednom řádku: rok → varianta │ přepínač vrstev
                (barevně odlišený) → Export PDF vpravo. Kontext (rok/varianta) je
                oddělen svislou čárou od navigace vrstvami. */}
            <div className="flex items-center gap-2 mb-3 flex-wrap flex-shrink-0">
                {/* Výběr roku */}
                <div className="flex items-center gap-1">
                    <button onClick={() => setRok(rok - 1)} aria-label="Předchozí rok"
                        className="p-1.5 rounded-lg hover:opacity-70 transition-opacity" style={{ color: 'var(--text-secondary)' }}>
                        <ChevronLeft size={18} />
                    </button>
                    <div className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                        {rok}
                    </div>
                    <button onClick={() => setRok(rok + 1)} aria-label="Další rok"
                        className="p-1.5 rounded-lg hover:opacity-70 transition-opacity" style={{ color: 'var(--text-secondary)' }}>
                        <ChevronRight size={18} />
                    </button>
                </div>

                {/* Výběr + správa varianty scénáře */}
                {isAdmin && (
                    <FinanceAopVarianty rok={rok} variantaId={variantaId} setVariantaId={setVariantaId} isDark={isDark} cardStyle={cardStyle} />
                )}

                {/* Oddělovač kontext → vrstvy */}
                <div style={{ width: 1, height: 22, background: 'var(--border-default)', margin: '0 3px' }} />

                {/* Přepínač vrstev — barevně odlišený pro orientaci */}
                {VRSTVY.map((v, i) => {
                    const Icon = v.Icon;
                    const active = vrstva === v.key;
                    const c = v.color;
                    return (
                        <button key={v.key} onClick={() => setVrstva(v.key)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                            style={{
                                background: active ? `${c}14` : 'var(--bg-secondary)',
                                color: active ? c : 'var(--text-secondary)',
                                border: `1px solid ${active ? c + '66' : 'var(--border-default)'}`,
                            }}>
                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
                                style={{ background: active ? c : `${c}26`, color: active ? '#fff' : c }}>
                                {i + 1}
                            </span>
                            <Icon size={14} style={{ color: c }} />
                            {v.label}
                        </button>
                    );
                })}

                <button onClick={exportPdf} title="Export celého plánu do PDF"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ marginLeft: 'auto', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                    <FileDown size={14} /> Export PDF
                </button>
            </div>

            {vrstva === 'prijmy'     && <FinanceAopPrijmy rok={rok} variantaId={variantaId} cardStyle={cardStyle} isDark={isDark} />}
            {vrstva === 'material'   && <FinanceAopMaterial rok={rok} variantaId={variantaId} cardStyle={cardStyle} isDark={isDark} />}
            {vrstva === 'personal'   && <FinanceAopPersonal rok={rok} variantaId={variantaId} cardStyle={cardStyle} isDark={isDark} />}
            {vrstva === 'energie'    && <FinanceAopEnergie rok={rok} variantaId={variantaId} cardStyle={cardStyle} isDark={isDark} />}
            {vrstva === 'ostatni'    && <FinanceAopOstatni rok={rok} variantaId={variantaId} cardStyle={cardStyle} isDark={isDark} />}
            {vrstva === 'souhrn'     && <FinanceAopSouhrn rok={rok} variantaId={variantaId} cardStyle={cardStyle} isDark={isDark} />}
            </div>{/* konec levého sloupce */}

            {/* Pravý panel statistik AOP — užší (tabulky mají 12 měsíců, potřebují šířku) */}
            <div className="w-full xl:w-[clamp(300px,20vw,360px)] flex-shrink-0 min-h-0">
                <AopStatistikyPanel rok={rok} variantaId={variantaId} cardStyle={cardStyle} />
            </div>
        </div>
    );
}
